import { useState } from 'react';
import { offsite as Api, type OffsiteConfig } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonPage } from '../components/Shimmer';
import { Field, Row } from '../components/Field';

const when = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T') + 'Z');
  return isNaN(d.valueOf()) ? v
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const size = (n: number) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1000)} KB`);

export function Backups() {
  const { data, error, loading, reload } = useAsync((signal) => Api.load(signal));
  const [f, setF] = useState<OffsiteConfig | null>(null);
  const [secretKey, setSecretKey] = useState('');
  const [status, setStatus] = useState('');
  const [formError, setFormError] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);

  const cfg = f ?? data?.config ?? null;
  const set = (k: keyof OffsiteConfig, v: unknown) =>
    setF((p) => ({ ...(p ?? data!.config), [k]: v }) as OffsiteConfig);

  async function save() {
    if (!cfg) return;
    setFormError('');
    setStatus('Saving…');
    try {
      await Api.save({
        endpoint: cfg.endpoint, region: cfg.region, bucket: cfg.bucket, prefix: cfg.prefix,
        access_key_id: cfg.access_key_id, path_style: cfg.path_style, keep: cfg.keep, state: cfg.state,
        ...(secretKey ? { secret_key: secretKey } : {}),
      });
      setSecretKey('');
      setStatus('Saved');
      setF(null);
      await reload();
    } catch (e) { setStatus(''); setFormError((e as Error).message); }
  }

  async function test() {
    setFormError('');
    setStatus('Testing…');
    try { await Api.test(); setStatus('It worked.'); await reload(); }
    catch (e) { setStatus(''); setFormError((e as Error).message); }
  }

  if (loading && !data) return <SkeletonPage />;
  if (error) return <div className="pg"><div className="err">Could not load the backup settings — {error.message}</div></div>;
  if (!data || !cfg) return null;

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Backups</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            The server takes a local backup on a schedule. Off-site copies are what survive the server itself.
          </p>
        </div>
        <span className="tg" style={{
          background: data.enabled ? 'rgba(62,207,142,.14)' : 'rgba(240,100,91,.13)',
        }}>{data.enabled ? 'off-site on' : 'local only'}</span>
      </div>

      {!data.enabled ? (
        <div className="crd" style={{ padding: '16px 20px', marginBottom: 16, borderColor: 'rgba(240,100,91,.4)' }}>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', margin: 0, lineHeight: 1.6 }}>
            <strong>Everything is on one machine.</strong> The database, the uploaded media and the backups
            all live on the same disk, so a disk failure loses all three at once.
            {data.missing.length ? ` Still needed: ${data.missing.join(', ')}.` : ''}
          </p>
        </div>
      ) : null}

      {formError ? <div className="err">{formError}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="kh" style={{ marginBottom: 14 }}>Where copies go</div>
        <Row>
          <Field label="Endpoint" hint="An S3-compatible host — Backblaze B2, Wasabi, or S3 itself.">
            <input className="inp" value={cfg.endpoint} placeholder="https://s3.eu-central-003.backblazeb2.com"
              onChange={(e) => set('endpoint', e.target.value)} /></Field>
          <Field label="Region"><input className="inp" value={cfg.region}
            onChange={(e) => set('region', e.target.value)} /></Field>
          <Field label="Bucket"><input className="inp" value={cfg.bucket}
            onChange={(e) => set('bucket', e.target.value)} /></Field>
        </Row>
        <Row>
          <Field label="Access key id"><input className="inp" value={cfg.access_key_id}
            onChange={(e) => set('access_key_id', e.target.value)} /></Field>
          <Field
            label="Secret key"
            hint={cfg.has_secret_key ? 'Stored. Leave blank to keep it.' : 'Not set.'}
          >
            <input className="inp" type="password" autoComplete="new-password" disabled={!data.can_store_secrets}
              value={secretKey} placeholder={cfg.has_secret_key ? 'Unchanged' : ''}
              onChange={(e) => setSecretKey(e.target.value)} /></Field>
          <Field label="Keep how many" hint="Older copies are removed after each upload.">
            <input className="inp" type="number" min={1} value={cfg.keep || ''}
              onChange={(e) => set('keep', Number(e.target.value))} /></Field>
        </Row>
        <Row cols={3}>
          <Field label="Prefix"><input className="inp" value={cfg.prefix}
            onChange={(e) => set('prefix', e.target.value)} /></Field>
          <Field label="State">
            <select className="inp" value={cfg.state} onChange={(e) => set('state', e.target.value)}>
              <option value="active">active</option>
              <option value="off">off</option>
            </select></Field>
          <div className="fld" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 22 }}>
            <input type="checkbox" checked={cfg.path_style} onChange={(e) => set('path_style', e.target.checked)} />
            <span style={{ fontSize: 13.5 }}>Path-style URLs</span>
          </div>
        </Row>

        <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="b bo" type="button" onClick={() => void test()}>Test the connection</button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>
            {status || (cfg.tested_ok ? `Tested ${when(cfg.tested_at)} — it worked.`
              : cfg.last_error ? cfg.last_error : '')}
          </span>
          <button className="b bg" type="button" onClick={() => void save()}>Save</button>
        </div>
      </div>

      {/* The passphrase is the whole point of the arrangement: archives are
          encrypted before they leave, so the host cannot read them — and
          neither can you, without this. */}
      <div className="crd" style={{ padding: '20px 22px', marginTop: 18, borderColor: 'rgba(var(--goldrgb), .28)' }}>
        <div className="kh" style={{ marginBottom: 6 }}>Archive passphrase</div>
        <p className="hint" style={{ marginBottom: 12 }}>
          Archives are encrypted before they are uploaded. <strong>Copy this into a password manager now.</strong>{' '}
          Without it the copies already off-site cannot be opened — by anyone, including you.
        </p>
        {cfg.has_passphrase ? (
          <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
            <code style={{ flex: 1, padding: '11px 14px', border: '1px solid var(--line)', borderRadius: 11,
              background: 'rgba(var(--wash), calc(.03 * var(--washx)))', fontSize: 13.5, letterSpacing: '.04em' }}>
              {showPassphrase ? (cfg.passphrase || '(cannot be read — the key changed)') : '••••••••••••'}
            </code>
            <button className="b bo" type="button" style={{ padding: '9px 14px', fontSize: 12 }}
              onClick={() => setShowPassphrase((v) => !v)}>{showPassphrase ? 'Hide' : 'Show'}</button>
            {showPassphrase && cfg.passphrase ? (
              <button className="b bo" type="button" style={{ padding: '9px 14px', fontSize: 12 }}
                onClick={() => void navigator.clipboard.writeText(cfg.passphrase)}>Copy</button>
            ) : null}
          </div>
        ) : (
          <p className="muted" style={{ fontSize: 13 }}>
            None yet — one is generated the first time an archive is uploaded.
          </p>
        )}
      </div>

      <div className="crd" style={{ padding: '20px 22px', marginTop: 18 }}>
        <div className="rowbar" style={{ marginBottom: 10 }}>
          <div className="kh">Copies off-site</div>
          {cfg.last_upload_at ? (
            <span style={{ fontSize: 12, color: 'var(--tx3)' }}>
              last upload {when(cfg.last_upload_at)} · {cfg.last_upload_name}
            </span>
          ) : null}
        </div>
        {data.list_error ? <div className="err">{data.list_error}</div> : null}
        {data.objects.length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>
            {data.enabled ? 'Nothing uploaded yet.' : 'Off-site copies are switched off.'}
          </p>
        ) : (
          <div className="tblx">
            <table className="tbl">
              <thead><tr><th>Archive</th><th>Size</th><th>Uploaded</th></tr></thead>
              <tbody>
                {data.objects.map((o) => (
                  <tr key={o.key}>
                    <td>{o.key}</td>
                    <td className="muted">{size(o.size)}</td>
                    <td className="muted">{when(o.uploaded)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
